import { type ReactNode } from 'react';
import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import { DateField, Show, useRecordContext } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { FiredAlert } from '../dataProvider';
import { alertTypeLabel } from './alertsModel';

/**
 * The fired-alert detail (Show) for the trail (D-internal-alerts, Phase 9), the events-explorer
 * EventShow analogue. Shows the top-level fields (type, fired, market, channels delivered) plus
 * the full TRIGGER context bag PRETTY-PRINTED (what tripped the alert). READ-ONLY: a fired alert
 * is an immutable record; there is no action here and no re-send affordance (delivery is the
 * backend seam).
 */

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

const AlertShowLayout = () => {
  const record = useRecordContext<FiredAlert>();
  if (!record) return null;

  return (
    <Box sx={{ maxWidth: 760 }} data-testid="alert-detail">
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Fired alert
          </Typography>
          <Typography variant="h6" component="h1" sx={{ mb: 2 }}>
            {alertTypeLabel(record.alertType)}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
            }}
          >
            <Field label="Alert type">{alertTypeLabel(record.alertType)}</Field>
            <Field label="Fired">
              <DateField source="firedAt" showTime />
            </Field>
            <Field label="Market">{String(record.market).toUpperCase()}</Field>
            <Field label="Channels delivered">
              {(record.channelsDelivered ?? []).join(', ') || 'none'}
            </Field>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* The trigger context bag, pretty-printed (what tripped the alert). */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Trigger
          </Typography>
          <Box
            component="pre"
            data-testid="alert-trigger"
            sx={{
              m: 0,
              p: 2,
              borderRadius: 1,
              bgcolor: 'action.hover',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {JSON.stringify(record.trigger ?? {}, null, 2)}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

/** The detail view. Exported for testing without the access wrapper. */
export const AlertShowView = () => (
  <Show title="Fired alert" actions={false} resource="firedAlerts">
    <AlertShowLayout />
  </Show>
);

/** Gated entry point registered as the firedAlerts Resource's show. */
export const AlertShow = () => (
  <RequireAccess roles={['super_admin', 'fraud', 'support']}>
    <AlertShowView />
  </RequireAccess>
);
